import {
  Box,
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  SxProps,
} from '@mui/material'
import { ReactNode, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Else, If, Then } from 'react-if'

import { Icon } from '@/components'
import { minLength, required, VailTextField } from '@/components/reactHookForm'
import { useAddHistoryItem } from '@/hooks/useHistory/useAddHistoryItem'
import { safeCypressID } from '@/utils'
import { CreateVailHistoryInput, CreateVailHistoryMutation } from '@/utils/api'
import { WithChildren } from '@/utils/types'
/**
 * @prop {string[]} omitFields - the fields that will not be saved from the
 * object passed to the `dataToSave` prop. The default ones are 'id', 'createdOn', 'updatedOn', 'publishedAt', 'history',
 * @prop {React.ReactNode} [desc]
 * @prop {React.ReactNode} [selectFlightPrompt]
 */
export type PublishDialogProps = WithChildren<{
  onEnd?: (param: CreateVailHistoryMutation) => void
  buttonSx?: SxProps
  buttonText?: string
  buttonProps?: ButtonProps
  dataToSave?: Record<string, any>
  queryKey: (string | undefined)[]
  extraInfo: Partial<CreateVailHistoryInput>
  onSubmit?: (
    params: Partial<CreateVailHistoryInput>
  ) => Record<string, any> | Promise<Record<string, any>>
  customDialog?: ReactNode
  customDialogOnSubmit?: (
    data: Partial<CreateVailHistoryInput>
  ) =>
    | Partial<CreateVailHistoryInput>
    | Promise<Partial<CreateVailHistoryInput>>
}>

export const PublishDialog = (props: PublishDialogProps) => {
  const [open, setOpen] = useState(false)
  const addHistory = useAddHistoryItem(props.queryKey)

  const form = useForm<Partial<CreateVailHistoryInput>>({
    defaultValues: { name: '' },
  })
  const openModal = () => {
    form.reset({})
    setOpen(true)
  }
  const closeModal = () => {
    setOpen(false)
    form.reset({})
  }

  const submit = async (data: Partial<CreateVailHistoryInput>) => {
    const { name, notes } = data
    try {
      const submitResult = await props?.onSubmit?.({ name, notes })
      const newHistoryItem = await addHistory.mutateAsync(
        JSON.parse(
          JSON.stringify({
            ...props.extraInfo,
            ...submitResult,
            data: JSON.stringify({ ...props.dataToSave, ...submitResult }),
            name: name ?? '',
            notes,
            ...((await props?.customDialogOnSubmit?.(data)) ?? {}),
          })
        )
      )
      return props?.onEnd?.(newHistoryItem)
    } finally {
      closeModal()
    }
  }

  const buttonText = props.buttonText ?? 'New Version'
  return (
    <>
      <Button
        {...props.buttonProps}
        id={safeCypressID('publish button')}
        startIcon={
          props.buttonProps?.startIcon ||
          (buttonText === 'New Version' ? <Icon name="add" /> : undefined)
        }
        sx={
          props.buttonSx ?? {
            alignSelf: 'flex-end',
            bottom: 32,
            position: 'fixed',
            right: 32,
          }
        }
        variant="contained"
        onClick={openModal}
      >
        {buttonText}
      </Button>
      <Dialog open={open} onClose={closeModal}>
        <DialogTitle>Create Version</DialogTitle>
        <FormProvider {...form}>
          <Box component="form" onSubmit={form.handleSubmit(submit)}>
            <DialogContent>
              <If condition={!!props.customDialog}>
                <Then>{props.customDialog}</Then>
                <Else>
                  <VailTextField
                    control={form.control}
                    label="Version name"
                    name="name"
                    rules={{
                      minLength: minLength(1),
                      required: required,
                    }}
                  />
                  <VailTextField
                    multiline
                    control={form.control}
                    label="Version Notes"
                    maxRows={6}
                    minRows={4}
                    name="notes"
                    rules={{
                      minLength: undefined,
                      required: undefined,
                    }}
                    sx={{ mt: 2 }}
                  />
                  {props.children}
                </Else>
              </If>
            </DialogContent>
            <DialogActions>
              <Button
                id={safeCypressID('publish cancel')}
                variant="text"
                onClick={closeModal}
              >
                Cancel
              </Button>
              <Button
                id={safeCypressID('publish submit')}
                loading={form.formState.isSubmitting}
                type="submit"
                variant="contained"
              >
                Publish
              </Button>
            </DialogActions>
          </Box>
        </FormProvider>
      </Dialog>
    </>
  )
}
